import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Camera, Check, Loader2, RefreshCw, X } from "lucide-react";

export default function SelfieCamera({ open, onClose, onCapture }) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [status, setStatus] = useState("starting");
  const [captured, setCaptured] = useState(null);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const startStream = async () => {
    setStatus("starting");
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus("error");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setStatus("live");
    } catch {
      setStatus("error");
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    startStream();
    return stopStream;
  }, [open]);

  useEffect(() => () => captured && URL.revokeObjectURL(captured.url), [captured]);

  if (!open) return null;

  const close = () => {
    stopStream();
    setCaptured(null);
    onClose();
  };

  const capture = () => {
    const video = videoRef.current;
    if (!video?.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        stopStream();
        setCaptured({ blob, url: URL.createObjectURL(blob) });
        setStatus("captured");
      },
      "image/jpeg",
      0.9
    );
  };

  const retake = () => {
    setCaptured(null);
    startStream();
  };

  const confirmPhoto = () => {
    onCapture(new File([captured.blob], `selfie-${Date.now()}.jpg`, { type: "image/jpeg" }));
    close();
  };

  const handleFallbackFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    onCapture(file);
    close();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between bg-gradient-to-r from-[#B82A1E] to-[#8a1f16] px-5 py-4 text-white">
          <h3 className="flex items-center gap-2 font-serif text-xl">
            <Camera className="h-5 w-5" />
            {t("mantraDiksha.camera.title")}
          </h3>
          <button type="button" onClick={close} className="rounded-full p-1 hover:bg-white/20">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="relative aspect-[3/4] bg-black">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`h-full w-full -scale-x-100 object-cover ${status === "live" ? "" : "hidden"}`}
          />

          {status === "captured" && captured && (
            <img src={captured.url} alt={t("mantraDiksha.upload.preview")} className="h-full w-full object-cover" />
          )}

          {status === "starting" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/80">
              <Loader2 className="mb-3 h-8 w-8 animate-spin" />
              <span className="text-sm">{t("mantraDiksha.camera.starting")}</span>
            </div>
          )}

          {status === "error" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center text-white">
              <Camera className="h-10 w-10 text-white/60" />
              <p className="text-sm text-white/80">{t("mantraDiksha.camera.error")}</p>
              <input
                type="file"
                accept="image/*"
                capture="user"
                onChange={handleFallbackFile}
                className="hidden"
                id="selfie-fallback"
              />
              <label
                htmlFor="selfie-fallback"
                className="cursor-pointer rounded-lg bg-white px-4 py-2 text-sm font-medium text-[#B82A1E]"
              >
                {t("mantraDiksha.camera.openCamera")}
              </label>
            </div>
          )}
        </div>

        <div className="flex gap-3 p-4">
          {status === "captured" ? (
            <>
              <button
                type="button"
                onClick={retake}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl border-2 border-[#B82A1E]/30 py-3 font-medium text-[#B82A1E] hover:bg-[#B82A1E]/5"
              >
                <RefreshCw className="h-4 w-4" />
                {t("mantraDiksha.camera.retake")}
              </button>
              <button
                type="button"
                onClick={confirmPhoto}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#B82A1E] to-[#8a1f16] py-3 font-bold text-white"
              >
                <Check className="h-4 w-4" />
                {t("mantraDiksha.camera.usePhoto")}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={close}
                className="flex-1 rounded-xl border-2 border-gray-200 py-3 font-medium text-gray-600 hover:bg-gray-50"
              >
                {t("mantraDiksha.camera.cancel")}
              </button>
              <button
                type="button"
                onClick={capture}
                disabled={status !== "live"}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#B82A1E] to-[#8a1f16] py-3 font-bold text-white disabled:cursor-not-allowed disabled:from-gray-300 disabled:to-gray-300 disabled:text-gray-500"
              >
                <Camera className="h-4 w-4" />
                {t("mantraDiksha.camera.capture")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
